import type { Platform, StepKind, TestStep } from '@1qa/shared';

export const KIND_META: Record<StepKind, { label: string; verb: string; hint: string; examples: string[] }> = {
  open: {
    label: 'Open page',
    verb: 'Open',
    hint: 'A path on your site (like /login) or a full URL. For mobile apps this launches the app.',
    examples: ['/', '/login', '/products/shoes'],
  },
  act: {
    label: 'Do something',
    verb: 'Do',
    hint: 'Describe the action like you would to a colleague. 1QA figures out where to click or type.',
    examples: ['click the "Sign in" button', 'type "demo@1qa.io" into the Email field', 'add the first product to the cart'],
  },
  assert: {
    label: 'Check something',
    verb: 'Check',
    hint: 'Describe what should be visible or true. The test fails if it is not.',
    examples: ['the dashboard greets the user by name', 'the cart shows 1 item', 'an error says the password is wrong'],
  },
  wait: {
    label: 'Wait',
    verb: 'Wait',
    hint: 'Pause in milliseconds (1000 = 1 second). Use sparingly - 1QA already waits for pages to load.',
    examples: ['500', '1000', '3000'],
  },
};

/** Plain-English sentence for one step, used in the live preview and reports. */
export function describeStep(step: TestStep, platform: Platform = 'WEB'): string {
  const v = step.value.trim();
  switch (step.kind) {
    case 'open':
      if (platform !== 'WEB') return 'Launch the app';
      return v ? `Open ${v === '/' ? 'the home page' : v}` : 'Open a page';
    case 'act':
      return v ? capitalise(v) : 'Do something…';
    case 'assert':
      return v ? `Make sure ${v}` : 'Check something…';
    case 'wait': {
      const ms = Number(v);
      if (!Number.isFinite(ms) || ms <= 0) return 'Wait';
      return ms >= 1000 ? `Wait ${+(ms / 1000).toFixed(1)} second${ms === 1000 ? '' : 's'}` : `Wait ${ms} ms`;
    }
  }
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
